// Web Audio drum synthesis engine + metronome clicks.
import { Mp3Encoder } from "@breezystack/lamejs";
export const VOICE_ORDER = ["CC", "RD", "HH", "HT", "MT", "FT", "SD", "BD", "CB", "TM", "CP", "RS", "RB"];
export const VOICE_LABELS = {
  CC: "Crash",
  RD: "Ride",
  HH: "Hi-Hat",
  HT: "High Tom",
  MT: "Mid Tom",
  FT: "Floor Tom",
  SD: "Snare",
  BD: "Bass",
  CB: "Cowbell",
  TM: "Tambourine",
  CP: "Clap",
  RS: "Rimshot",
  RB: "Ride Bell",
};

// Expand a section's unique measures by its `repeat` count (how many bars that
// part lasts in the song). Builder songs pre-expand and omit `repeat` (=> 1).
// Expand a section's repeats; an optional `ending` replaces the final bar(s) on the last pass.
// Each returned measure carries `src` = index into [...measures, ...ending].
export function sectionMeasures(sec) {
  const reps = Math.max(1, Math.min(16, parseInt(sec && sec.repeat, 10) || 1));
  const base = (sec && sec.measures) || [];
  const ending = reps > 1 && Array.isArray(sec.ending) ? sec.ending.slice(0, base.length) : [];
  const cut = base.length - ending.length;
  const out = [];
  for (let r = 0; r < reps; r++) {
    const last = r === reps - 1;
    base.forEach((m, i) => {
      if (last && i >= cut) out.push({ ...ending[i - cut], src: base.length + i - cut });
      else out.push({ ...m, src: i });
    });
  }
  return out;
}

// Real acoustic one-shot samples (CC0 / public domain) bundled under /samples.
// Voices not listed (CB, TM, CP) fall back to synthesis.
const SAMPLE_FILES = {
  BD: "kick", SD: "snare", HT: "high-tom", MT: "mid-tom", FT: "floor-tom",
  HH: "closed-hat", OH: "open-hat", CC: "crash", RD: "ride",
  RS: "snare-rimshot", RB: "ride-bell",
};

class DrumEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this._vol = 0.9;
    this.accentLevel = 1;
    this.sampleBuffers = {};
    this.sampleOffsets = {};
    this._rawSamples = {};
    this._decoding = {};
    this._prefetchSamples();
  }

  async _prefetchSamples() {
    await Promise.all(Object.entries(SAMPLE_FILES).map(async ([voice, name]) => {
      try {
        const res = await fetch(`/samples/${name}.mp3`);
        if (res.ok) this._rawSamples[voice] = await res.arrayBuffer();
      } catch (e) { /* fallback to synthesis */ }
    }));
    this._decodeSamples();
  }

  // Seconds of leading silence (MP3 encoder padding etc.) so hits land exactly on the beat.
  _onset(buf) {
    const d = buf.getChannelData(0);
    let peak = 0;
    for (let i = 0; i < d.length; i++) { const a = Math.abs(d[i]); if (a > peak) peak = a; }
    const th = peak * 0.02;
    for (let i = 0; i < d.length; i++) if (Math.abs(d[i]) > th) return Math.max(0, i - Math.round(buf.sampleRate * 0.002)) / buf.sampleRate;
    return 0;
  }

  _decodeSamples() {
    if (!this.ctx) return;
    Object.entries(this._rawSamples).forEach(([voice, raw]) => {
      if (this.sampleBuffers[voice] || this._decoding[voice]) return;
      this._decoding[voice] = true;
      try {
        this.ctx.decodeAudioData(
          raw.slice(0),
          (buf) => { this.sampleBuffers[voice] = buf; this.sampleOffsets[voice] = this._onset(buf); },
          () => { this._decoding[voice] = false; }
        );
      } catch (e) { this._decoding[voice] = false; }
    });
  }

  ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this._vol;
      this.master.connect(this.ctx.destination);
      this._decodeSamples();
    }
    if (this.ctx.state === "suspended" && typeof this.ctx.startRendering !== "function") this.ctx.resume();
    return this.ctx;
  }

  get now() {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  setVolume(v) {
    this._vol = v;
    if (this.master) this.master.gain.value = v;
  }

  _noise(duration) {
    const ctx = this.ctx;
    const size = Math.max(1, Math.floor(ctx.sampleRate * duration));
    const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < size; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    return src;
  }

  // voice: one of VOICE_ORDER, plus "OH" for open hi-hat. accent -> louder.
  trigger(voice, time, accent = false, gain = 1) {
    this.ensure();
    if (gain <= 0) return [];
    const ctx = this.ctx;
    if (time == null) time = ctx.currentTime;
    const out = this.master;
    const nodes = [];
    const vel = (accent ? 0.72 + 0.28 * this.accentLevel : 0.72) * gain;

    // Prefer the real acoustic sample when available (falls back to synthesis).
    const sbuf = this.sampleBuffers[voice];
    if (sbuf) {
      const src = ctx.createBufferSource();
      src.buffer = sbuf;
      const gs = ctx.createGain();
      gs.gain.value = Math.min(1.6, vel);
      src.connect(gs);
      gs.connect(out);
      const off = this.sampleOffsets[voice] || 0;
      src.start(time, off);
      src.stop(time + sbuf.duration - off + 0.05);
      nodes.push(src);
      return nodes;
    }

    const g = ctx.createGain();
    g.connect(out);

    const toneTom = (freq) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.setValueAtTime(freq, time);
      o.frequency.exponentialRampToValueAtTime(freq * 0.5, time + 0.18);
      g.gain.setValueAtTime(vel, time);
      g.gain.exponentialRampToValueAtTime(0.001, time + 0.3);
      o.connect(g);
      o.start(time);
      o.stop(time + 0.32);
      nodes.push(o);
    };

    const HAT_RATIOS = [2, 3, 4.16, 5.43, 6.79, 8.21];

    // Metallic cluster of inharmonic square waves -> bandpass -> highpass.
    const metal = (fundamental, ratios, dur, bpFreq, bpQ, hpFreq, level) => {
      const gm = ctx.createGain();
      gm.connect(out);
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = bpFreq;
      bp.Q.value = bpQ;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = hpFreq;
      bp.connect(hp);
      hp.connect(gm);
      gm.gain.setValueAtTime(0.0001, time);
      gm.gain.exponentialRampToValueAtTime(vel * level, time + 0.002);
      gm.gain.exponentialRampToValueAtTime(0.0003, time + dur);
      ratios.forEach((r) => {
        const o = ctx.createOscillator();
        o.type = "square";
        o.frequency.value = fundamental * r;
        o.connect(bp);
        o.start(time);
        o.stop(time + dur + 0.02);
        nodes.push(o);
      });
    };

    // Filtered noise burst with its own gain (wash / stick attack).
    const noiseLayer = (dur, hpFreq, level) => {
      const gn = ctx.createGain();
      gn.connect(out);
      const nz = this._noise(dur);
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = hpFreq;
      nz.connect(hp);
      hp.connect(gn);
      gn.gain.setValueAtTime(vel * level, time);
      gn.gain.exponentialRampToValueAtTime(0.0008, time + dur);
      nz.start(time);
      nz.stop(time + dur + 0.02);
      nodes.push(nz);
    };

    // Pitched tone with its own gain (optionally gliding).
    const tone = (type, freq, dur, level, glideTo) => {
      const gt = ctx.createGain();
      gt.connect(out);
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.setValueAtTime(freq, time);
      if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, time + dur);
      gt.gain.setValueAtTime(vel * level, time);
      gt.gain.exponentialRampToValueAtTime(0.0008, time + dur);
      o.connect(gt);
      o.start(time);
      o.stop(time + dur + 0.02);
      nodes.push(o);
    };

    switch (voice) {
      case "BD": {
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.setValueAtTime(145, time);
        o.frequency.exponentialRampToValueAtTime(45, time + 0.12);
        g.gain.setValueAtTime(vel, time);
        g.gain.exponentialRampToValueAtTime(0.001, time + 0.28);
        o.connect(g);
        o.start(time);
        o.stop(time + 0.3);
        nodes.push(o);
        break;
      }
      case "SD": {
        const n = this._noise(0.2);
        const hp = ctx.createBiquadFilter();
        hp.type = "highpass";
        hp.frequency.value = 1400;
        n.connect(hp);
        hp.connect(g);
        g.gain.setValueAtTime(vel * 0.9, time);
        g.gain.exponentialRampToValueAtTime(0.001, time + 0.18);
        n.start(time);
        n.stop(time + 0.2);
        nodes.push(n);
        const o = ctx.createOscillator();
        o.type = "triangle";
        o.frequency.value = 190;
        const g2 = ctx.createGain();
        g2.connect(out);
        g2.gain.setValueAtTime(vel * 0.5, time);
        g2.gain.exponentialRampToValueAtTime(0.001, time + 0.12);
        o.connect(g2);
        o.start(time);
        o.stop(time + 0.14);
        nodes.push(o);
        break;
      }
      case "HH": {
        metal(40, HAT_RATIOS, 0.075, 10000, 1.2, 7000, 0.95);
        break;
      }
      case "OH": {
        metal(40, HAT_RATIOS, 0.42, 10000, 1.2, 7000, 0.8);
        break;
      }
      case "CC": {
        metal(38, HAT_RATIOS, 1.4, 6000, 0.9, 3200, 0.55);
        noiseLayer(1.1, 5000, 0.3);
        break;
      }
      case "RD": {
        metal(55, HAT_RATIOS, 0.55, 9000, 1.4, 6500, 0.4);
        noiseLayer(0.05, 7000, 0.45);
        tone("triangle", 2600, 0.08, 0.25);
        break;
      }
      case "HT":
        toneTom(260);
        break;
      case "MT":
        toneTom(190);
        break;
      case "FT":
        toneTom(120);
        break;
      case "CB": {
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = 2200;
        bp.Q.value = 1.2;
        bp.connect(g);
        g.gain.setValueAtTime(vel * 0.45, time);
        g.gain.exponentialRampToValueAtTime(0.001, time + 0.35);
        [560, 845].forEach((f) => {
          const o = ctx.createOscillator();
          o.type = "square";
          o.frequency.value = f;
          o.connect(bp);
          o.start(time);
          o.stop(time + 0.37);
          nodes.push(o);
        });
        break;
      }
      case "TM": {
        const jingle = (t0, dur, level, freq) => {
          const gn = ctx.createGain();
          gn.connect(out);
          const nz = this._noise(dur);
          const bpj = ctx.createBiquadFilter();
          bpj.type = "bandpass";
          bpj.frequency.value = freq;
          bpj.Q.value = 4.0;
          nz.connect(bpj);
          bpj.connect(gn);
          gn.gain.setValueAtTime(vel * level, t0);
          gn.gain.exponentialRampToValueAtTime(0.0006, t0 + dur);
          nz.start(t0);
          nz.stop(t0 + dur + 0.02);
          nodes.push(nz);
        };
        jingle(time, 0.22, 0.6, 9000);
        jingle(time + 0.018, 0.12, 0.3, 11000);
        metal(900, [1, 2.76, 4.18, 6.05], 0.18, 11000, 2.5, 8500, 0.22);
        break;
      }
      case "CP": {
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = 1200;
        bp.Q.value = 1.0;
        bp.connect(g);
        [0, 0.012, 0.024].forEach((off) => {
          g.gain.setValueAtTime(vel * 0.6, time + off);
          g.gain.exponentialRampToValueAtTime(0.001, time + off + 0.02);
        });
        g.gain.setValueAtTime(vel * 0.5, time + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, time + 0.18);
        const n = this._noise(0.25);
        n.connect(bp);
        n.start(time);
        n.stop(time + 0.25);
        nodes.push(n);
        break;
      }
      case "RS": {
        noiseLayer(0.035, 2000, 0.9);
        tone("triangle", 340, 0.1, 0.7);
        tone("square", 440, 0.05, 0.3);
        break;
      }
      case "RB": {
        metal(620, [1, 1.73, 2.47, 3.11, 4.21], 0.5, 5200, 1.1, 2600, 0.5);
        noiseLayer(0.04, 6000, 0.35);
        tone("triangle", 3200, 0.06, 0.2);
        break;
      }
      default:
        break;
    }
    return nodes;
  }

  click(time, accent = false) {
    this.ensure();
    const ctx = this.ctx;
    if (time == null) time = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "square";
    o.frequency.value = accent ? 1800 : 1100;
    g.gain.setValueAtTime(accent ? 0.5 : 0.3, time);
    g.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
    o.connect(g);
    g.connect(this.master);
    o.start(time);
    o.stop(time + 0.06);
    return [o];
  }

  // Short ascending triad fanfare for celebrations.
  celebrate(time) {
    this.ensure();
    const ctx = this.ctx;
    if (time == null) time = ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const t = time + i * 0.09;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.4, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      o.connect(g);
      g.connect(this.master);
      o.start(t);
      o.stop(t + 0.37);
    });
  }

  // Offline-render a full tab/song arrangement to an AudioBuffer (honours mixer mute/vol + swing + per-part tempo).
  async renderToBuffer(tab, opts = {}) {
    const spm = tab.steps_per_measure || 16;
    const beats = parseInt(tab.time_signature, 10) || 4;
    const baseBpm = tab.bpm || 120;
    const stepsPerBeat = Math.max(1, Math.round(spm / beats));
    const swing = tab.mix && typeof tab.mix.swing === "number" ? tab.mix.swing : 0;
    const ghostGain = tab.mix && typeof tab.mix.ghostVol === "number" ? tab.mix.ghostVol / 100 : GHOST_GAIN;
    const mixer = (tab.mix && tab.mix.mixer) || {};
    const eighthSteps = Math.max(1, Math.round(stepsPerBeat / 2));

    const steps = [];     // events per step
    const stepDurs = [];  // seconds per step (per-section tempo + ramp)
    (tab.sections || []).forEach((sec) => {
      const startBpm = sec.bpm || baseBpm;
      const endBpm = sec.ramp || startBpm;
      const ease = sec.rampCurve === "ease";
      const secMeasures = sectionMeasures(sec);
      const secSteps = secMeasures.length * spm;
      let j = 0;
      secMeasures.forEach((m) => {
        const n = m.steps || spm;
        const mspb = Math.max(1, Math.round(n / beats));
        for (let i = 0; i < n; i++) {
          const frac = secSteps > 1 ? (j * spm) / n / (secSteps - 1) : 0;
          const ef = ease ? frac * frac * (3 - 2 * frac) : Math.min(1, frac);
          const stepBpm = startBpm + (endBpm - startBpm) * ef;
          const stepDur = ((60 / stepBpm) * beats) / n;
          const events = [];
          VOICE_ORDER.forEach((v) => {
            const cell = parseCell((m.lanes[v] || "")[i]);
            if (!cell.hit) return;
            const mx = mixer[v] || {};
            if (mx.mute) return;
            const gain = (typeof mx.vol === "number" ? mx.vol : 1) * (cell.ghost ? ghostGain : 1);
            if (gain <= 0) return;
            const voice = v === "HH" && cell.open ? "OH" : v;
            events.push({ voice, accent: !cell.ghost && (i % mspb === 0 || cell.accent), gain });
          });
          steps.push(events);
          stepDurs.push(stepDur);
        }
        j += spm;
      });
    });

    const totalSteps = steps.length;
    if (totalSteps === 0) return null;

    const totalDur = stepDurs.reduce((a, b) => a + b, 0);
    const sampleRate = 44100;
    const duration = totalDur + 1.5;
    const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    const offline = new OAC(2, Math.ceil(duration * sampleRate), sampleRate);

    const liveCtx = this.ctx;
    const liveMaster = this.master;
    this.ctx = offline;
    this.master = offline.createGain();
    this.master.gain.value = this._vol;
    this.master.connect(offline.destination);

    try {
      let t = 0;
      for (let s = 0; s < totalSteps; s++) {
        const stepDur = stepDurs[s];
        const eighthIndex = (s % stepsPerBeat) / eighthSteps;
        const swingDelay = (swing / 100) * ((eighthSteps * stepDur) / 3);
        const off = Number.isInteger(eighthIndex) && eighthIndex % 2 === 1 ? swingDelay : 0;
        steps[s].forEach((e) => this.trigger(e.voice, t + off, e.accent, e.gain));
        t += stepDur;
      }
      return await offline.startRendering();
    } finally {
      this.ctx = liveCtx;
      this.master = liveMaster;
    }
  }

  async renderToWav(tab, opts = {}) {
    const buffer = await this.renderToBuffer(tab, opts);
    if (!buffer) return null;
    applyFadeOut(buffer, opts);
    return audioBufferToWavBlob(buffer);
  }

  async renderToMp3(tab, opts = {}) {
    const buffer = await this.renderToBuffer(tab, opts);
    if (!buffer) return null;
    applyFadeOut(buffer, opts);
    return audioBufferToMp3Blob(buffer);
  }
}

function applyFadeOut(buffer, opts = {}) {
  if (!opts.fadeOut) return;
  const sr = buffer.sampleRate;
  const fadeSec = opts.fadeSec || 2.0;
  const fadeSamples = Math.min(buffer.length, Math.floor(fadeSec * sr));
  if (fadeSamples <= 0) return;
  const start = buffer.length - fadeSamples;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < fadeSamples; i++) {
      const g = 1 - i / fadeSamples;
      data[start + i] *= g * g; // equal-power-ish smoother taper
    }
  }
}

function audioBufferToWavBlob(buffer) {
  const numCh = buffer.numberOfChannels;
  const sr = buffer.sampleRate;
  const dataLen = buffer.length * numCh * 2;
  const ab = new ArrayBuffer(44 + dataLen);
  const view = new DataView(ab);
  const writeStr = (off, str) => { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)); };
  let o = 0;
  writeStr(o, "RIFF"); o += 4;
  view.setUint32(o, 36 + dataLen, true); o += 4;
  writeStr(o, "WAVE"); o += 4;
  writeStr(o, "fmt "); o += 4;
  view.setUint32(o, 16, true); o += 4;
  view.setUint16(o, 1, true); o += 2;
  view.setUint16(o, numCh, true); o += 2;
  view.setUint32(o, sr, true); o += 4;
  view.setUint32(o, sr * numCh * 2, true); o += 4;
  view.setUint16(o, numCh * 2, true); o += 2;
  view.setUint16(o, 16, true); o += 2;
  writeStr(o, "data"); o += 4;
  view.setUint32(o, dataLen, true); o += 4;
  const channels = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));
  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numCh; c++) {
      let sample = Math.max(-1, Math.min(1, channels[c][i]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(o, sample, true); o += 2;
    }
  }
  return new Blob([view], { type: "audio/wav" });
}

function audioBufferToMp3Blob(buffer) {
  const numCh = Math.min(2, buffer.numberOfChannels);
  const sr = buffer.sampleRate;
  const kbps = 128;
  const enc = new Mp3Encoder(numCh, sr, kbps);
  const toInt16 = (arr) => {
    const out = new Int16Array(arr.length);
    for (let i = 0; i < arr.length; i++) {
      const s = Math.max(-1, Math.min(1, arr[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  };
  const left = toInt16(buffer.getChannelData(0));
  const right = numCh > 1 ? toInt16(buffer.getChannelData(1)) : left;
  const blockSize = 1152;
  const data = [];
  for (let i = 0; i < left.length; i += blockSize) {
    const l = left.subarray(i, i + blockSize);
    const r = right.subarray(i, i + blockSize);
    const buf = numCh > 1 ? enc.encodeBuffer(l, r) : enc.encodeBuffer(l);
    if (buf.length > 0) data.push(new Uint8Array(buf));
  }
  const end = enc.flush();
  if (end.length > 0) data.push(new Uint8Array(end));
  return new Blob(data, { type: "audio/mpeg" });
}

export const engine = new DrumEngine();

// Parse a lane char -> { hit, accent, open, ghost }
export const GHOST_GAIN = 0.3;
export function parseCell(ch) {
  if (!ch || ch === "." || ch === "-" || ch === " ") return { hit: false };
  if (ch === "o" || ch === "O") return { hit: true, accent: true, open: true };
  if (ch === "X") return { hit: true, accent: true };
  if (ch === "g" || ch === "G") return { hit: true, accent: false, ghost: true };
  return { hit: true, accent: false };
}

// Build an ASCII representation of the tab for copy/export.
export function tabToAscii(tab) {
  if (!tab || !tab.sections) return "";
  const spm = tab.steps_per_measure || 16;
  const lines = [];
  lines.push(`${tab.title} — ${tab.artist}`);
  lines.push(`BPM ${tab.bpm} | ${tab.time_signature} | ${tab.difficulty}`);
  lines.push("");
  tab.sections.forEach((section) => {
    lines.push(`[${section.name}]`);
    const secMs = sectionMeasures(section);
    VOICE_ORDER.forEach((v) => {
      const used = secMs.some((m) =>
        (m.lanes[v] || "").split("").some((c) => parseCell(c).hit)
      );
      if (!used && !["HH", "SD", "BD"].includes(v)) return;
      let row = `${v}|`;
      secMs.forEach((m) => {
        const lane = m.lanes[v] || ".".repeat(spm);
        row += lane.split("").map((c) => (parseCell(c).hit ? (parseCell(c).open ? "o" : parseCell(c).ghost ? "g" : "x") : "-")).join("") + "|";
      });
      lines.push(row);
    });
    lines.push("");
  });
  if (tab.notes) {
    lines.push("Notes: " + tab.notes);
  }
  return lines.join("\n");
}

function writeVarLen(value) {
  let buffer = value & 0x7f;
  const bytes = [];
  while ((value >>= 7)) {
    buffer <<= 8;
    buffer |= (value & 0x7f) | 0x80;
  }
  while (true) {
    bytes.push(buffer & 0xff);
    if (buffer & 0x80) buffer >>= 8;
    else break;
  }
  return bytes;
}

// Build a General MIDI drum track (channel 10) from the structured tab.
export function tabToMidiBytes(tab) {
  const spm = tab.steps_per_measure || 16;
  const beats = parseInt(tab.time_signature, 10) || 4;
  const bpm = tab.bpm || 120;
  const PPQ = 480;
  const barTicks = PPQ * beats;
  const ticksPerStep = Math.max(1, Math.round(barTicks / spm));
  const GM = { BD: 36, SD: 38, HH: 42, OH: 46, HT: 48, MT: 45, FT: 41, CC: 49, RD: 51,
    CB: 56, TM: 54, CP: 39, RS: 37, RB: 53 };

  let global = 0;
  const hits = [];
  tab.sections.forEach((sec) => {
    sectionMeasures(sec).forEach((m) => {
      const base = global;
      global += barTicks;
      const n = m.steps || spm;
      for (let i = 0; i < n; i++) {
        VOICE_ORDER.forEach((v) => {
          const cell = parseCell((m.lanes[v] || "")[i]);
          if (cell.hit) {
            const note = v === "HH" && cell.open ? GM.OH : GM[v];
            if (note == null) return;
            hits.push({ tick: base + Math.round((i * barTicks) / n), note, vel: cell.ghost ? 35 : cell.accent ? 110 : 80 });
          }
        });
      }
    });
  });

  const events = [];
  const mpq = Math.round(60000000 / bpm);
  events.push({ tick: 0, order: 0, data: [0xff, 0x51, 0x03, (mpq >> 16) & 0xff, (mpq >> 8) & 0xff, mpq & 0xff] });
  hits.forEach((h) => {
    events.push({ tick: h.tick, order: 2, data: [0x99, h.note, h.vel] });
    events.push({ tick: h.tick + Math.floor(ticksPerStep * 0.9), order: 1, data: [0x89, h.note, 0] });
  });
  events.sort((a, b) => a.tick - b.tick || a.order - b.order);

  const track = [];
  let last = 0;
  events.forEach((e) => {
    track.push(...writeVarLen(e.tick - last), ...e.data);
    last = e.tick;
  });
  track.push(...writeVarLen(0), 0xff, 0x2f, 0x00);

  const header = [0x4d, 0x54, 0x68, 0x64, 0, 0, 0, 6, 0, 0, 0, 1, (PPQ >> 8) & 0xff, PPQ & 0xff];
  const len = track.length;
  const trkHead = [0x4d, 0x54, 0x72, 0x6b, (len >> 24) & 0xff, (len >> 16) & 0xff, (len >> 8) & 0xff, len & 0xff];
  return new Uint8Array([...header, ...trkHead, ...track]);
}
